/**
 * Arkivering av konkurranser når de er ferdige
 */

import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import { APP_ID, COMPETITION_MODES } from '../constants/config';
import { calculateTugStats } from './calculations';

/**
 * Sjekk om konkurranse er ferdig
 */
const isCompetitionFinished = (competition, leaderboard) => {
  // LONGEST_DISTANCE: Sjekk sluttdato
  if (competition.mode === COMPETITION_MODES.LONGEST_DISTANCE && competition.endDate) {
    return new Date(competition.endDate) < new Date();
  }
  
  // TUG_OF_WAR: Sjekk om ett lag har nådd målet
  if (competition.mode === COMPETITION_MODES.TUG_OF_WAR && competition.goal) {
    const stats = calculateTugStats(competition, leaderboard);
    const difference = stats.teamA - stats.teamB;
    return Math.abs(difference) >= competition.goal;
  }
  
  // FIRST_TO_GOAL: Sjekk om noen har nådd målet
  if (competition.mode === COMPETITION_MODES.FIRST_TO_GOAL && competition.goal) {
    const hasWinner = competition.participants.some(id => {
      const participant = leaderboard.find(l => l.id === id);
      if (!participant) return false;
      const km = participant.competitionKm?.[competition.id] || 0;
      return km >= competition.goal;
    });
    return hasWinner;
  }
  
  return false;
};

/**
 * Sjekk om konkurranse er ferdig og arkiver hvis nødvendig
 */
export const checkAndArchiveCompetition = async (competition, leaderboard) => {
  // Sjekk om konkurransen er ferdig
  if (!isCompetitionFinished(competition, leaderboard)) return false;
  
  // Hvis allerede arkivert, returner
  if (competition.archived) return false;
  
  try {
    // Hent deltakernes data fra leaderboard
    const participants = competition.participants || [];
    const finalResults = participants
      .map(userId => {
        const userData = leaderboard.find(l => l.id === userId);
        // Bruk competitionKm for denne spesifikke konkurransen
        const competitionKm = userData?.competitionKm?.[competition.id] || 0;
        return {
          userId,
          name: userData?.name || 'Ukjent',
          avatar: userData?.avatar || '🏃',
          totalKm: competitionKm,
          team: competition.teams?.[userId] // For tug of war
        };
      })
      .sort((a, b) => b.totalKm - a.totalKm);
    
    // Oppdater konkurransen med arkivert data
    const compRef = doc(db, 'artifacts', APP_ID, 'public', 'data', 'competitions', competition.id);
    await updateDoc(compRef, {
      archived: true,
      archivedAt: new Date().toISOString(),
      finalResults: finalResults
    });
    
    console.log('Competition archived:', competition.id);
    return true;
  } catch (error) {
    console.error('Error archiving competition:', error);
    return false;
  }
};

/**
 * Hent resultater for konkurranse (arkivert eller live)
 */
export const getCompetitionResults = (competition, leaderboard) => {
  // Hvis arkivert, bruk finalResults
  if (competition.archived && competition.finalResults) {
    return competition.finalResults;
  }
  
  // Ellers, hent live data
  const participants = competition.participants || [];
  return participants
    .map(userId => {
      const userData = leaderboard.find(l => l.id === userId);
      return {
        userId,
        name: userData?.name || 'Ukjent',
        avatar: userData?.avatar || '🏃',
        totalKm: userData?.totalKm || 0
      };
    })
    .sort((a, b) => b.totalKm - a.totalKm);
};
